import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { BASIC_POLL_STATUSES, BASIC_POLL_VOTE_TYPES, resolveBasicPollVoteType } from "../basic-polls/constants";
import { hasSubmittedVote } from "../basic-polls/vote-submission";
import {
  resolveBasicPollDeadline,
  isBasicPollDeadlineOpen,
} from "../voting-deadlines";
import {
  VOTE_VISIBILITY,
  resolveVoteAnonymization,
  resolveHideVoterIdentities,
  resolveHideVoterIdentitiesForVisibility,
  resolveVoteVisibility,
} from "../vote-visibility";
import { createReminderSettingsSnapshot } from "../../features/reminders/reminder-settings";

const DELETE_BATCH_SIZE = 450;
const PARENT_TYPE_COLLECTIONS = {
  group: "questingGroups",
  scheduler: "schedulers",
};
const BASIC_POLL_SERVER_FALLBACK_CODES = new Set([
  "functions/unavailable",
  "functions/not-found",
  "failed-precondition",
]);

export const groupBasicPollsRef = (groupId) =>
  collection(db, "questingGroups", groupId, "basicPolls");

export const groupBasicPollRef = (groupId, pollId) =>
  doc(db, "questingGroups", groupId, "basicPolls", pollId);

export const groupBasicPollVotesRef = (groupId, pollId) =>
  collection(db, "questingGroups", groupId, "basicPolls", pollId, "votes");

export function resolveBasicPollParentCollection(parentType) {
  return PARENT_TYPE_COLLECTIONS[parentType] || null;
}

export const basicPollRef = (parentType, parentId, pollId) => {
  const parentCollection = resolveBasicPollParentCollection(parentType);
  if (!parentCollection || !parentId || !pollId) return null;
  return doc(db, parentCollection, parentId, "basicPolls", pollId);
};

export const basicPollVotesRef = (parentType, parentId, pollId) => {
  const parentCollection = resolveBasicPollParentCollection(parentType);
  if (!parentCollection || !parentId || !pollId) return null;
  return collection(db, parentCollection, parentId, "basicPolls", pollId, "votes");
};

export const basicPollVoteRef = (parentType, parentId, pollId, userId) => {
  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!votesRef || !userId) return null;
  return doc(votesRef, userId);
};

function mapSnapshotDocs(snapshot) {
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

function sanitizePollCreateData(pollData = {}) {
  const voteVisibility = resolveVoteVisibility(pollData?.voteVisibility);
  const normalized = {
    ...pollData,
    voteVisibility,
    voteAnonymization: resolveVoteAnonymization(pollData?.voteAnonymization),
    hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
      pollData?.hideVoterIdentities,
      voteVisibility
    ),
    votesAllSubmitted: false,
  };
  if (Object.prototype.hasOwnProperty.call(pollData, "reminders")) {
    normalized.reminders = createReminderSettingsSnapshot(pollData.reminders);
  }
  return normalized;
}

function sanitizePollUpdateData(updates = {}) {
  const hasVoteVisibility = Object.prototype.hasOwnProperty.call(updates || {}, "voteVisibility");
  const hasVoteAnonymization = Object.prototype.hasOwnProperty.call(
    updates || {},
    "voteAnonymization"
  );
  const hasHideVoterIdentities = Object.prototype.hasOwnProperty.call(
    updates || {},
    "hideVoterIdentities"
  );
  const hasReminders = Object.prototype.hasOwnProperty.call(updates || {}, "reminders");
  if (!hasVoteVisibility && !hasVoteAnonymization && !hasHideVoterIdentities && !hasReminders) {
    return updates;
  }

  const normalized = { ...updates };
  if (hasVoteVisibility) {
    normalized.voteVisibility = resolveVoteVisibility(updates?.voteVisibility);
  }
  if (hasVoteAnonymization) {
    normalized.voteAnonymization = resolveVoteAnonymization(updates?.voteAnonymization);
  }
  if (hasHideVoterIdentities) {
    normalized.hideVoterIdentities = resolveHideVoterIdentitiesForVisibility(
      updates?.hideVoterIdentities,
      normalized.voteVisibility
    );
  } else if (
    hasVoteVisibility &&
    resolveVoteVisibility(normalized.voteVisibility) === VOTE_VISIBILITY.FULL
  ) {
    normalized.hideVoterIdentities = resolveHideVoterIdentities(false);
  }
  if (hasReminders) {
    normalized.reminders = createReminderSettingsSnapshot(updates.reminders);
  }
  return normalized;
}

// Structural fields are the ones that change the MEANING of an already-cast vote
// (which option an id refers to, how many may be selected, whether write-in
// counts). Title/description/deadline/notes/privacy are NOT structural.
const STRUCTURAL_SETTINGS_KEYS = ["voteType", "allowMultiple", "maxSelections", "allowWriteIn"];

export const POLL_STRUCTURE_LOCKED_MESSAGE =
  "Votes have already been cast — poll options and vote type can't be changed. Reset votes first.";

// Defense-in-depth CLIENT data-layer backstop for the edit-after-votes
// structural lock (NOT server enforcement). The UI is the primary guard; this
// function (used by both updateBasicPoll and updateEmbeddedBasicPoll) throws
// LOUD if a write would change a structural field on a voted poll, so a future
// feature can't silently corrupt vote meaning without surfacing an error. Per
// the project's guardrails-not-fortress model, hand-crafted data-layer leaks
// are an accepted residual — this catches the builder mistake, not the attacker.
function hasDottedStructuralKey(updates = {}) {
  // Dot-path field updates (e.g. { "settings.voteType": ... }) bypass the nested
  // `settings` object, so detect them explicitly.
  return Object.keys(updates || {}).some(
    (key) =>
      key.startsWith("options.") ||
      STRUCTURAL_SETTINGS_KEYS.some((sk) => key === `settings.${sk}`)
  );
}

function updateTouchesStructure(updates = {}) {
  if (Array.isArray(updates?.options)) return true;
  if (hasDottedStructuralKey(updates)) return true;
  const settings = updates?.settings;
  if (settings && typeof settings === "object") {
    return STRUCTURAL_SETTINGS_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(settings, key)
    );
  }
  return false;
}

function normalizeOptionsStruct(rawOptions) {
  const options = Array.isArray(rawOptions) ? rawOptions : [];
  return options
    .map((option, index) => ({
      id: String(option?.id || `option-${index}`),
      order: Number.isFinite(option?.order) ? Number(option.order) : index,
      label: String(option?.label || ""),
    }))
    .sort((left, right) => left.order - right.order);
}

function optionsStructEqual(left, right) {
  const a = normalizeOptionsStruct(left);
  const b = normalizeOptionsStruct(right);
  if (a.length !== b.length) return false;
  // Compare the RELATIVE sequence (both sides are already sorted by order), not
  // absolute order magnitudes — a stored poll with non-contiguous orders must
  // not make a pure title rename falsely trip the structural-lock backstop. A
  // genuine reorder still changes the sorted id sequence and is caught.
  return a.every((option, index) =>
    option.id === b[index].id &&
    option.label === b[index].label
  );
}

function normalizeMaxSelectionsValue(value) {
  return Number.isFinite(value) ? Number(value) : null;
}

function settingsStructEqual(currentSettings = {}, incomingSettings = {}) {
  const current = {
    voteType: resolveBasicPollVoteType(currentSettings?.voteType),
    allowMultiple: currentSettings?.allowMultiple === true,
    maxSelections: normalizeMaxSelectionsValue(currentSettings?.maxSelections),
    allowWriteIn: currentSettings?.allowWriteIn === true,
  };
  for (const key of STRUCTURAL_SETTINGS_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(incomingSettings, key)) continue;
    let incoming;
    if (key === "voteType") {
      incoming = resolveBasicPollVoteType(incomingSettings[key]);
    } else if (key === "maxSelections") {
      incoming = normalizeMaxSelectionsValue(incomingSettings[key]);
    } else {
      incoming = incomingSettings[key] === true;
    }
    if (incoming !== current[key]) return false;
  }
  return true;
}

async function assertUpdatePreservesStructure(parentType, parentId, pollId, updates) {
  if (!updateTouchesStructure(updates)) return;
  const pollRef = basicPollRef(parentType, parentId, pollId);
  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!pollRef || !votesRef) return;
  const pollSnap = await getDoc(pollRef);
  if (!pollSnap.exists()) return;
  const currentPoll = pollSnap.data() || {};
  const currentSettings = currentPoll.settings || {};
  const voteType = resolveBasicPollVoteType(currentSettings.voteType);
  const allowWriteIn =
    voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE &&
    currentSettings.allowWriteIn === true;
  const votesSnap = await getDocs(votesRef);
  const voteDocs = mapSnapshotDocs(votesSnap);
  const hasVotes = voteDocs.some((voteDoc) =>
    hasSubmittedVote(voteType, allowWriteIn, voteDoc)
  );
  if (!hasVotes) return;

  const optionsChanged =
    Array.isArray(updates.options) &&
    !optionsStructEqual(updates.options, currentPoll.options);
  const settingsChanged =
    updates.settings &&
    typeof updates.settings === "object" &&
    !settingsStructEqual(currentSettings, updates.settings);
  // A dot-path structural update can't be safely diffed against the current
  // shape, so on a voted poll we conservatively block it (the app never uses
  // this shape — pure future-builder guard).
  if (optionsChanged || settingsChanged || hasDottedStructuralKey(updates)) {
    throw new Error(POLL_STRUCTURE_LOCKED_MESSAGE);
  }
}

function isPollDeadlineOpen(pollData = {}) {
  return isBasicPollDeadlineOpen(pollData);
}

function isPermissionDeniedError(error) {
  const code = String(error?.code || "").toLowerCase();
  return code.includes("permission-denied");
}

async function callBasicPollServerAction(actionName, payload) {
  try {
    const functions = getFunctions();
    const action = httpsCallable(functions, actionName);
    const result = await action(payload);
    return result?.data || null;
  } catch (error) {
    const code = String(error?.code || "");
    const message = String(error?.message || "");
    const shouldFallback =
      BASIC_POLL_SERVER_FALLBACK_CODES.has(code) ||
      message.includes("No Firebase App") ||
      message.includes("no-app");

    if (shouldFallback) {
      return null;
    }

    throw error;
  }
}

async function deleteDocRefsInBatches(refs = []) {
  if (refs.length === 0) return;

  const commits = [];
  for (let index = 0; index < refs.length; index += DELETE_BATCH_SIZE) {
    const batch = writeBatch(db);
    refs.slice(index, index + DELETE_BATCH_SIZE).forEach((ref) => batch.delete(ref));
    commits.push(batch.commit());
  }

  await Promise.all(commits);
}

async function deleteBasicPollWithVotes(parentType, parentId, pollId) {
  const pollRef = basicPollRef(parentType, parentId, pollId);
  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!pollRef || !votesRef) return;

  const votesSnap = await getDocs(votesRef);
  const refsToDelete = votesSnap.docs.map((voteDoc) => voteDoc.ref);
  refsToDelete.push(pollRef);

  await deleteDocRefsInBatches(refsToDelete);
}

export async function createBasicPoll(groupId, pollData = {}, options = {}) {
  if (!groupId) return null;
  const normalizedPollData = sanitizePollCreateData(pollData);

  const useServer = options.useServer !== false;
  if (useServer) {
    const response = await callBasicPollServerAction("createBasicPoll", {
      parentType: "group",
      parentId: groupId,
      pollData: normalizedPollData,
    });
    if (response?.pollId) {
      return response.pollId;
    }
  }

  const created = await addDoc(groupBasicPollsRef(groupId), {
    ...normalizedPollData,
    status: normalizedPollData.status ?? BASIC_POLL_STATUSES.OPEN,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return created.id;
}

export async function fetchGroupBasicPolls(groupId) {
  if (!groupId) return [];
  const snapshot = await getDocs(groupBasicPollsRef(groupId));
  return mapSnapshotDocs(snapshot);
}

export async function fetchOpenGroupPollsWithoutVote(groupIds = [], userId) {
  const normalizedGroupIds = Array.from(new Set((groupIds || []).filter(Boolean)));
  if (!userId || normalizedGroupIds.length === 0) return [];

  const allResults = await Promise.all(
    normalizedGroupIds.map(async (groupId) => {
      const pollsSnapshot = await getDocs(
        query(groupBasicPollsRef(groupId), where("status", "==", BASIC_POLL_STATUSES.OPEN))
      );
      const openPolls = pollsSnapshot.docs.map((pollDoc) => ({
        id: pollDoc.id,
        ...pollDoc.data(),
      }));

      const unvoted = await Promise.all(
        openPolls.map(async (poll) => {
          if (!isPollDeadlineOpen(poll)) return null;

          const settings = poll?.settings || {};
          const voteType = resolveBasicPollVoteType(settings.voteType);
          const allowWriteIn =
            voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowWriteIn === true;
          const voteRef = basicPollVoteRef("group", groupId, poll.id, userId);
          const voteSnap = voteRef ? await getDoc(voteRef) : null;
          const myVote = voteSnap?.exists() ? voteSnap.data() || {} : null;
          const hasVoted = myVote ? hasSubmittedVote(voteType, allowWriteIn, myVote) : false;
          if (hasVoted) return null;

          return {
            ...poll,
            parentType: "group",
            parentId: groupId,
            pollId: poll.id,
          };
        })
      );

      return unvoted.filter(Boolean);
    })
  );

  return allResults.flat();
}

async function buildDashboardPollSummary(parentType, parentId, pollData, userId) {
  const settings = pollData?.settings || {};
  const voteType = resolveBasicPollVoteType(settings.voteType);
  const allowWriteIn =
    voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowWriteIn === true;
  let voteDocs = [];
  // statusKnown: did we read the FULL votes list (the per-person voted/pending
  // split is trustworthy) or only the viewer's own doc after a permission-denied
  // fallback (the split is NOT trustworthy — voterIds is own-only)? The Layer-A
  // COUNT stays authoritative via the denormalized counter regardless.
  let statusKnown = true;
  try {
    const votesSnapshot = await getDocs(basicPollVotesRef(parentType, parentId, pollData.id));
    voteDocs = mapSnapshotDocs(votesSnapshot);
  } catch (error) {
    if (!isPermissionDeniedError(error)) {
      throw error;
    }
    statusKnown = false;
    const ownVoteReference = basicPollVoteRef(parentType, parentId, pollData.id, userId);
    if (ownVoteReference) {
      const ownVoteSnapshot = await getDoc(ownVoteReference);
      voteDocs = ownVoteSnapshot.exists() ? [{ id: ownVoteSnapshot.id, ...ownVoteSnapshot.data() }] : [];
    }
  }
  const submittedVotes = voteDocs.filter((voteDoc) => hasSubmittedVote(voteType, allowWriteIn, voteDoc));
  const voterIds = submittedVotes.map((voteDoc) => voteDoc.id).filter(Boolean);
  const hasVoted = submittedVotes.some((voteDoc) => voteDoc.id === userId);

  // Layer A count (design §1/§5): source from the denormalized counter the
  // trigger keeps current, so it stays authoritative even when the votes list
  // read was permission-denied and voteDocs holds only the viewer's own doc
  // (own-only fallback would otherwise report 0/1 under restrictive privacy).
  // NOTE (design-doc Q-E): submittedVoteCount is guest-INCLUSIVE while the
  // participant denominator (eligibleCount) is participant-only, so a guest
  // voter can make the numerator exceed the denominator — an accepted residual
  // mirroring the scheduler-level fallback.
  const votedCount = Number.isFinite(pollData.submittedVoteCount)
    ? pollData.submittedVoteCount
    : submittedVotes.length;

  return {
    ...pollData,
    parentType,
    parentId,
    pollId: pollData.id,
    voteType,
    allowWriteIn,
    deadlineAt: resolveBasicPollDeadline(pollData),
    isDeadlineOpen: isPollDeadlineOpen(pollData),
    hasVoted,
    votedCount,
    voterIds,
    statusKnown,
    // Don't let the card trust the raw own-only voterIds when the list read was
    // denied: only show the per-person voted/pending stacks when the split is
    // known, OR for the viewer's own progress (design §5 Q-D).
    showVoteProgress: statusKnown || hasVoted,
  };
}

export async function fetchDashboardGroupBasicPolls(groupIds = [], userId) {
  const normalizedGroupIds = Array.from(new Set((groupIds || []).filter(Boolean)));
  if (!userId || normalizedGroupIds.length === 0) return [];

  const groupPolls = await Promise.all(
    normalizedGroupIds.map(async (groupId) => {
      const pollsSnapshot = await getDocs(groupBasicPollsRef(groupId));
      const polls = pollsSnapshot.docs.map((pollDoc) => ({ id: pollDoc.id, ...pollDoc.data() }));
      const summaries = await Promise.all(
        polls.map((pollData) => buildDashboardPollSummary("group", groupId, pollData, userId))
      );
      return summaries;
    })
  );

  return groupPolls.flat();
}

export async function updateBasicPoll(groupId, pollId, updates = {}) {
  if (!groupId || !pollId) return;
  const normalizedUpdates = sanitizePollUpdateData(updates);
  await assertUpdatePreservesStructure("group", groupId, pollId, normalizedUpdates);

  await updateDoc(groupBasicPollRef(groupId, pollId), {
    ...normalizedUpdates,
    updatedAt: serverTimestamp(),
  });
}

export async function finalizeBasicPoll(groupId, pollId) {
  return finalizeBasicPollForParent("group", groupId, pollId);
}

export async function reopenBasicPoll(groupId, pollId) {
  return reopenBasicPollForParent("group", groupId, pollId);
}

export async function finalizeBasicPollForParent(parentType, parentId, pollId) {
  if (!parentType || !parentId || !pollId) return;
  const pollRef = basicPollRef(parentType, parentId, pollId);
  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!pollRef || !votesRef) return;

  const response = await callBasicPollServerAction("finalizeBasicPoll", {
    parentType,
    parentId,
    pollId,
  });
  if (response?.status === BASIC_POLL_STATUSES.FINALIZED) return;

  // finalResults is server-only (set by the finalize callable under the
  // voteVisibility reveal predicate). The client fallback only advances status.
  await updateDoc(pollRef, {
    status: BASIC_POLL_STATUSES.FINALIZED,
    finalizedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function reopenBasicPollForParent(parentType, parentId, pollId) {
  if (!parentType || !parentId || !pollId) return;
  const pollRef = basicPollRef(parentType, parentId, pollId);
  if (!pollRef) return;

  const response = await callBasicPollServerAction("reopenBasicPoll", {
    parentType,
    parentId,
    pollId,
  });
  if (response?.status === BASIC_POLL_STATUSES.OPEN) return;

  // Clear any now-stale per-option results when reopening via the client
  // fallback. If the (finalized) poll had published finalResults the rule lets
  // the creator/manager clear them (resource.finalResults != null clause); if
  // it never had any (gated/withheld) the delete is a harmless no-op (the
  // request/resource finalResults are both absent → equality clause holds).
  await updateDoc(pollRef, {
    status: BASIC_POLL_STATUSES.OPEN,
    finalResults: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

export async function finalizeEmbeddedBasicPoll(schedulerId, pollId) {
  return finalizeBasicPollForParent("scheduler", schedulerId, pollId);
}

export async function reopenEmbeddedBasicPoll(schedulerId, pollId) {
  return reopenBasicPollForParent("scheduler", schedulerId, pollId);
}

function selectTieWinner(tiedIds = [], method) {
  if (!Array.isArray(tiedIds) || tiedIds.length === 0) return null;
  if (method === "random") {
    return tiedIds[Math.floor(Math.random() * tiedIds.length)] || tiedIds[0];
  }
  if (typeof method === "string" && tiedIds.includes(method)) {
    return method;
  }
  return tiedIds[0];
}

export async function breakBasicPollTieForParent(parentType, parentId, pollId, method) {
  if (!parentType || !parentId || !pollId) return null;
  const pollRef = basicPollRef(parentType, parentId, pollId);
  if (!pollRef) return null;

  const response = await callBasicPollServerAction("breakBasicPollTie", {
    parentType,
    parentId,
    pollId,
    method,
  });
  if (response?.winnerId || response?.winnerIds) return response;

  const pollSnap = await getDoc(pollRef);
  if (!pollSnap.exists()) return null;
  const pollData = pollSnap.data() || {};
  const finalResults = pollData.finalResults || {};
  const tiedIds = Array.isArray(finalResults.tiedIds) ? finalResults.tiedIds.filter(Boolean) : [];
  const winnerId = selectTieWinner(tiedIds, method);
  if (!winnerId) return null;

  const winnerIds = [winnerId];
  await updateDoc(pollRef, {
    finalResults: {
      ...finalResults,
      winnerIds,
      tiedIds: [],
      tieBreak: {
        method: typeof method === "string" && method ? method : "first",
        winnerId,
        appliedAt: serverTimestamp(),
      },
    },
    updatedAt: serverTimestamp(),
  });

  return { winnerId, winnerIds };
}

export async function deleteBasicPoll(groupId, pollId, options = {}) {
  const useServer = options.useServer === true;
  if (useServer) {
    const response = await callBasicPollServerAction("removeBasicPoll", {
      parentType: "group",
      parentId: groupId,
      pollId,
    });
    if (response?.removed) return;
  }

  await deleteBasicPollWithVotes("group", groupId, pollId);
}

export async function resetBasicPollVotes(parentType, parentId, pollId, options = {}) {
  const useServer = options.useServer === true;
  if (useServer) {
    const response = await callBasicPollServerAction("resetBasicPollVotes", {
      parentType,
      parentId,
      pollId,
    });
    if (response && Number.isFinite(response.deletedVotes)) {
      return;
    }
  }

  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!votesRef) return;
  const votesSnap = await getDocs(votesRef);
  const voteRefs = votesSnap.docs.map((voteDoc) => voteDoc.ref);
  await deleteDocRefsInBatches(voteRefs);
}

export function subscribeToGroupPolls(groupId, callback, onError) {
  if (!groupId) {
    if (callback) callback([]);
    return () => {};
  }

  const pollsQuery = query(groupBasicPollsRef(groupId), orderBy("createdAt", "desc"));
  return onSnapshot(
    pollsQuery,
    (snapshot) => {
      if (callback) callback(mapSnapshotDocs(snapshot));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export function subscribeToBasicPoll(groupId, pollId, callback, onError) {
  if (!groupId || !pollId) {
    if (callback) callback(null);
    return () => {};
  }

  return onSnapshot(
    groupBasicPollRef(groupId, pollId),
    (snapshot) => {
      if (!callback) return;
      callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export async function createEmbeddedBasicPoll(schedulerId, pollData = {}, options = {}) {
  if (!schedulerId) return null;
  const normalizedPollData = sanitizePollCreateData(pollData);

  const useServer = options.useServer !== false;
  if (useServer) {
    const response = await callBasicPollServerAction("createBasicPoll", {
      parentType: "scheduler",
      parentId: schedulerId,
      pollData: normalizedPollData,
    });
    if (response?.pollId) {
      return response.pollId;
    }
  }

  const pollsRef = collection(db, "schedulers", schedulerId, "basicPolls");
  const created = await addDoc(pollsRef, {
    ...normalizedPollData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return created.id;
}

export async function fetchEmbeddedBasicPolls(schedulerId) {
  if (!schedulerId) return [];
  const snapshot = await getDocs(collection(db, "schedulers", schedulerId, "basicPolls"));
  return mapSnapshotDocs(snapshot);
}

export async function fetchRequiredEmbeddedPollsWithoutVote(schedulerIds = [], userId) {
  const normalizedSchedulerIds = Array.from(new Set((schedulerIds || []).filter(Boolean)));
  if (!userId || normalizedSchedulerIds.length === 0) return [];

  const allResults = await Promise.all(
    normalizedSchedulerIds.map(async (schedulerId) => {
      const pollsSnapshot = await getDocs(
        query(
          collection(db, "schedulers", schedulerId, "basicPolls"),
          where("required", "==", true)
        )
      );
      const requiredPolls = pollsSnapshot.docs.map((pollDoc) => ({
        id: pollDoc.id,
        ...pollDoc.data(),
      }));

      const unvoted = await Promise.all(
        requiredPolls.map(async (poll) => {
          if (poll?.status && poll.status !== BASIC_POLL_STATUSES.OPEN) return null;
          if (!isPollDeadlineOpen(poll)) return null;

          const settings = poll?.settings || {};
          const voteType = resolveBasicPollVoteType(settings.voteType);
          const allowWriteIn =
            voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && settings.allowWriteIn === true;
          const voteRef = basicPollVoteRef("scheduler", schedulerId, poll.id, userId);
          const voteSnap = voteRef ? await getDoc(voteRef) : null;
          const myVote = voteSnap?.exists() ? voteSnap.data() || {} : null;
          const hasVoted = myVote ? hasSubmittedVote(voteType, allowWriteIn, myVote) : false;
          if (hasVoted) return null;

          return {
            ...poll,
            parentType: "scheduler",
            parentId: schedulerId,
            pollId: poll.id,
          };
        })
      );

      return unvoted.filter(Boolean);
    })
  );

  return allResults.flat();
}

export async function fetchDashboardEmbeddedBasicPolls(schedulerIds = [], userId) {
  const normalizedSchedulerIds = Array.from(new Set((schedulerIds || []).filter(Boolean)));
  if (!userId || normalizedSchedulerIds.length === 0) return [];

  const schedulerPolls = await Promise.all(
    normalizedSchedulerIds.map(async (schedulerId) => {
      const pollsSnapshot = await getDocs(collection(db, "schedulers", schedulerId, "basicPolls"));
      const polls = pollsSnapshot.docs.map((pollDoc) => ({ id: pollDoc.id, ...pollDoc.data() }));
      const summaries = await Promise.all(
        polls.map((pollData) => buildDashboardPollSummary("scheduler", schedulerId, pollData, userId))
      );
      return summaries;
    })
  );

  return schedulerPolls.flat();
}

export async function updateEmbeddedBasicPoll(schedulerId, pollId, updates = {}) {
  if (!schedulerId || !pollId) return;
  const normalizedUpdates = sanitizePollUpdateData(updates);
  await assertUpdatePreservesStructure(
    "scheduler",
    schedulerId,
    pollId,
    normalizedUpdates
  );

  await updateDoc(doc(db, "schedulers", schedulerId, "basicPolls", pollId), {
    ...normalizedUpdates,
    updatedAt: serverTimestamp(),
  });
}

export async function notifyEmbeddedBasicPollRequiredChanged(schedulerId, pollId) {
  if (!schedulerId || !pollId) return null;
  return callBasicPollServerAction("notifyBasicPollRequiredChanged", {
    schedulerId,
    basicPollId: pollId,
  });
}

export async function fetchRequiredEmbeddedPollFinalizeSummary(schedulerId) {
  if (!schedulerId) return null;
  return callBasicPollServerAction("getRequiredEmbeddedPollFinalizeSummary", {
    schedulerId,
  });
}

export async function deleteEmbeddedBasicPoll(schedulerId, pollId, options = {}) {
  const useServer = options.useServer === true;
  if (useServer) {
    const response = await callBasicPollServerAction("removeBasicPoll", {
      parentType: "scheduler",
      parentId: schedulerId,
      pollId,
    });
    if (response?.removed) return;
  }

  await deleteBasicPollWithVotes("scheduler", schedulerId, pollId);
}

export async function reorderEmbeddedBasicPolls(schedulerId, pollIds = []) {
  if (!schedulerId) return;

  const normalizedIds = (pollIds || []).filter(Boolean);
  if (normalizedIds.length === 0) return;

  const commits = [];
  for (let index = 0; index < normalizedIds.length; index += DELETE_BATCH_SIZE) {
    const batch = writeBatch(db);
    normalizedIds.slice(index, index + DELETE_BATCH_SIZE).forEach((pollId, offset) => {
      batch.update(doc(db, "schedulers", schedulerId, "basicPolls", pollId), {
        order: index + offset,
        updatedAt: serverTimestamp(),
      });
    });
    commits.push(batch.commit());
  }

  await Promise.all(commits);
}

export function subscribeToEmbeddedBasicPolls(schedulerId, callback, onError) {
  if (!schedulerId) {
    if (callback) callback([]);
    return () => {};
  }

  const pollsQuery = query(
    collection(db, "schedulers", schedulerId, "basicPolls"),
    orderBy("order", "asc")
  );

  return onSnapshot(
    pollsQuery,
    (snapshot) => {
      if (callback) callback(mapSnapshotDocs(snapshot));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export async function cloneEmbeddedBasicPolls(
  sourceSchedulerId,
  targetSchedulerId,
  options = {}
) {
  if (!sourceSchedulerId || !targetSchedulerId) return;

  const clearVotes = options.clearVotes === true;
  const userId = options.userId || null;
  const votesByPollId = options.votesByPollId || {};
  const targetReminderSettings = options.reminders === undefined
    ? undefined
    : createReminderSettingsSnapshot(options.reminders);

  const sourcePolls = await fetchEmbeddedBasicPolls(sourceSchedulerId);
  const sortedPolls = [...sourcePolls].sort((left, right) => {
    const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
    const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return String(left?.title || "").localeCompare(String(right?.title || ""));
  });

  await Promise.all(
    sortedPolls.map((poll, index) => {
      const targetPollRef = doc(db, "schedulers", targetSchedulerId, "basicPolls", poll.id);
      const clonedPoll = {
        ...poll,
        // full_visibility ⇒ unhide, so a stale hide flag doesn't survive the copy.
        hideVoterIdentities: resolveHideVoterIdentitiesForVisibility(
          poll?.hideVoterIdentities,
          poll?.voteVisibility
        ),
        order: Number.isFinite(poll?.order) ? poll.order : index,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (targetReminderSettings !== undefined) {
        clonedPoll.reminders = targetReminderSettings;
      } else if (poll.reminders !== undefined) {
        clonedPoll.reminders = createReminderSettingsSnapshot(poll.reminders);
      }
      delete clonedPoll.id;
      delete clonedPoll.finalResults;
      delete clonedPoll.finalizedAt;
      delete clonedPoll.finalizedByUserId;
      delete clonedPoll.status;
      return setDoc(targetPollRef, clonedPoll);
    })
  );

  if (clearVotes || !userId) return;

  await Promise.all(
    sortedPolls.map(async (poll) => {
      const voteType = resolveBasicPollVoteType(poll?.settings?.voteType);
      const allowWriteIn =
        voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE && poll?.settings?.allowWriteIn === true;
      const sourceVote = votesByPollId[poll.id] || null;
      if (!sourceVote || !hasSubmittedVote(voteType, allowWriteIn, sourceVote)) return;

      const nextVote = {
        ...sourceVote,
        updatedAt: serverTimestamp(),
      };
      delete nextVote.id;
      const targetVoteRef = basicPollVoteRef("scheduler", targetSchedulerId, poll.id, userId);
      if (!targetVoteRef) return;
      await setDoc(targetVoteRef, nextVote, { merge: true });
    })
  );
}

export async function submitBasicPollVote(parentType, parentId, pollId, userId, voteData = {}) {
  const voteRef = basicPollVoteRef(parentType, parentId, pollId, userId);
  if (!voteRef) return;

  await setDoc(
    voteRef,
    {
      ...voteData,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function deleteBasicPollVote(parentType, parentId, pollId, userId) {
  const voteRef = basicPollVoteRef(parentType, parentId, pollId, userId);
  if (!voteRef) return;
  await deleteDoc(voteRef);
}

export function subscribeToBasicPollVotes(parentType, parentId, pollId, callback, onError) {
  const votesRef = basicPollVotesRef(parentType, parentId, pollId);
  if (!votesRef) {
    if (callback) callback([]);
    return () => {};
  }

  return onSnapshot(
    votesRef,
    (snapshot) => {
      if (callback) callback(mapSnapshotDocs(snapshot));
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}

export function subscribeToMyBasicPollVote(
  parentType,
  parentId,
  pollId,
  userId,
  callback,
  onError
) {
  const voteRef = basicPollVoteRef(parentType, parentId, pollId, userId);
  if (!voteRef) {
    if (callback) callback(null);
    return () => {};
  }

  return onSnapshot(
    voteRef,
    (snapshot) => {
      if (!callback) return;
      callback(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
    },
    (error) => {
      if (onError) onError(error);
    }
  );
}
